import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trophy, Flame, PiggyBank, Coins, Plus, Check, X, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SavingsChallenge, ChallengeType } from '@/lib/types';

// 52-week challenge: week N = $N (1+2+...+52 = $1,378)
const WEEK_TOTAL = 1378;

function getWeekNumber(startDate: string): number {
  const start = new Date(startDate);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  return Math.max(0, Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)));
}

function getNoSpendStreak(days: string[]): number {
  if (days.length === 0) return 0;
  const sorted = [...days].sort().reverse();
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  // Streak must include today or yesterday
  if (sorted[0] !== today && sorted[0] !== yesterday) return 0;
  let streak = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const curr = new Date(sorted[i]);
    const diff = (prev.getTime() - curr.getTime()) / 86400000;
    if (diff === 1) streak++;
    else break;
  }
  return streak;
}

export default function SavingsChallengesPage() {
  const { data, updateData } = useAuth();
  const challenges = data.savingsChallenges ?? [];
  const [createOpen, setCreateOpen] = useState(false);
  const [newType, setNewType] = useState<ChallengeType>('52week');
  const [newName, setNewName] = useState('');
  const [noSpendTarget, setNoSpendTarget] = useState('20');

  // Round-up simulation: sum up all expense round-ups for active roundup challenges
  const roundupSimTotal = useMemo(() => {
    const activeRoundup = challenges.find(c => c.type === 'roundup' && c.active);
    if (!activeRoundup) return 0;
    const startDate = new Date(activeRoundup.startDate);
    return data.transactions
      .filter(t => t.type === 'expense' && new Date(t.date) >= startDate)
      .reduce((sum, t) => {
        const rounded = Math.ceil(t.amount);
        return sum + (rounded - t.amount);
      }, 0);
  }, [data.transactions, challenges]);

  async function createChallenge() {
    const name = newName.trim() || (
      newType === '52week' ? '52-Week Challenge' :
      newType === 'roundup' ? 'Round-Up Savings' :
      'No-Spend Challenge'
    );
    const challenge: SavingsChallenge = {
      id: generateId(),
      type: newType,
      name,
      startDate: new Date().toISOString().split('T')[0],
      active: true,
      createdAt: new Date().toISOString(),
      ...(newType === '52week' ? { completedWeeks: new Array(52).fill(false) } : {}),
      ...(newType === 'roundup' ? { roundupTotal: 0 } : {}),
      ...(newType === 'nospend' ? { noSpendDays: [], noSpendTarget: parseInt(noSpendTarget) || 20 } : {}),
    };
    await updateData(prev => ({
      ...prev,
      savingsChallenges: [...(prev.savingsChallenges ?? []), challenge],
    }));
    setNewName('');
    setCreateOpen(false);
  }

  async function toggleWeek(challengeId: string, weekIdx: number) {
    await updateData(prev => ({
      ...prev,
      savingsChallenges: (prev.savingsChallenges ?? []).map(c => {
        if (c.id !== challengeId || !c.completedWeeks) return c;
        const weeks = [...c.completedWeeks];
        weeks[weekIdx] = !weeks[weekIdx];
        return { ...c, completedWeeks: weeks };
      }),
    }));
  }

  async function toggleNoSpendToday(challengeId: string) {
    const today = new Date().toISOString().split('T')[0];
    await updateData(prev => ({
      ...prev,
      savingsChallenges: (prev.savingsChallenges ?? []).map(c => {
        if (c.id !== challengeId) return c;
        const days = c.noSpendDays ?? [];
        const has = days.includes(today);
        return { ...c, noSpendDays: has ? days.filter(d => d !== today) : [...days, today] };
      }),
    }));
  }

  async function deleteChallenge(id: string) {
    await updateData(prev => ({
      ...prev,
      savingsChallenges: (prev.savingsChallenges ?? []).filter(c => c.id !== id),
    }));
  }

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.substring(0, 7);

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <Trophy className="h-6 w-6 text-accent" />
            Challenges
          </h1>
          <p className="text-sm text-muted-foreground">Gamified savings to build good habits</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="icon" variant="outline"><Plus className="h-4 w-4" /></Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start a Challenge</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {([
                  { type: '52week' as ChallengeType, label: '52-Week', icon: Coins, desc: 'Save $1 week 1, $2 week 2… $1,378 total' },
                  { type: 'roundup' as ChallengeType, label: 'Round-Up', icon: PiggyBank, desc: 'Simulated round-ups on every expense' },
                  { type: 'nospend' as ChallengeType, label: 'No-Spend', icon: Flame, desc: 'Track no-spend days & build streaks' },
                ]).map(opt => (
                  <button
                    key={opt.type}
                    onClick={() => setNewType(opt.type)}
                    className={cn(
                      'flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition-colors',
                      newType === opt.type ? 'border-primary bg-primary/5' : 'border-border'
                    )}
                  >
                    <opt.icon className={cn('h-5 w-5', newType === opt.type ? 'text-primary' : 'text-muted-foreground')} />
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {newType === '52week' && 'Save $1 in week 1, $2 in week 2… totalling $1,378 over a year.'}
                {newType === 'roundup' && 'Simulates rounding up every expense to the nearest dollar. Watch your virtual savings grow!'}
                {newType === 'nospend' && 'Mark days where you spend nothing. Build streaks and hit your monthly target.'}
              </p>
              <div>
                <Label>Challenge Name (optional)</Label>
                <Input placeholder="e.g. My 2025 Challenge" value={newName} onChange={e => setNewName(e.target.value)} className="mt-1" />
              </div>
              {newType === 'nospend' && (
                <div>
                  <Label>Monthly Target (days)</Label>
                  <Input type="number" value={noSpendTarget} onChange={e => setNoSpendTarget(e.target.value)} className="mt-1" />
                </div>
              )}
              <Button className="w-full" onClick={createChallenge}>Start Challenge</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {challenges.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No challenges yet. Start one to gamify your savings!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {challenges.filter(c => c.active).map(c => {
            if (c.type === '52week') {
              const weeks = c.completedWeeks ?? new Array(52).fill(false);
              const completedCount = weeks.filter(Boolean).length;
              const savedAmount = weeks.reduce((sum, done, i) => done ? sum + (i + 1) : sum, 0);
              const currentWeek = Math.min(getWeekNumber(c.startDate), 51);
              const pct = (savedAmount / WEEK_TOTAL) * 100;

              return (
                <Card key={c.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Coins className="h-4 w-4 text-accent" />
                        {c.name}
                      </CardTitle>
                      <button onClick={() => deleteChallenge(c.id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {completedCount}/52 weeks · {formatAUD(savedAmount)} of {formatAUD(WEEK_TOTAL)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <Progress value={Math.min(pct, 100)} className="h-2 mb-3" />
                    <div className="grid grid-cols-13 gap-1">
                      {weeks.map((done, i) => (
                        <button
                          key={i}
                          onClick={() => toggleWeek(c.id, i)}
                          className={cn(
                            'h-6 w-full rounded text-[9px] font-medium transition-colors',
                            done ? 'bg-success text-success-foreground' :
                            i <= currentWeek ? 'bg-muted hover:bg-primary/20 text-muted-foreground' :
                            'bg-muted/50 text-muted-foreground/50'
                          )}
                          title={`Week ${i + 1}: ${formatAUD(i + 1)}`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Current week: {currentWeek + 1} · Save {formatAUD(currentWeek + 1)} this week
                    </p>
                  </CardContent>
                </Card>
              );
            }

            if (c.type === 'roundup') {
              return (
                <Card key={c.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <PiggyBank className="h-4 w-4 text-primary" />
                        {c.name}
                      </CardTitle>
                      <button onClick={() => deleteChallenge(c.id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-4">
                      <p className="text-sm text-muted-foreground mb-1">Virtual round-up savings</p>
                      <p className="font-display text-3xl font-bold text-primary">{formatAUD(roundupSimTotal)}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Every expense is rounded up to the nearest dollar. The difference accumulates here.
                      </p>
                    </div>
                    {/* Show last 5 round-ups */}
                    <div className="space-y-1 mt-2">
                      {data.transactions
                        .filter(t => t.type === 'expense' && new Date(t.date) >= new Date(c.startDate))
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .slice(0, 5)
                        .map(t => {
                          const roundup = Math.ceil(t.amount) - t.amount;
                          if (roundup === 0) return null;
                          return (
                            <div key={t.id} className="flex items-center justify-between text-xs rounded bg-muted/50 px-2 py-1">
                              <span className="text-muted-foreground truncate">{t.description.replace(/^\[basiq:[^\]]+\]\s*/, '') || t.category}</span>
                              <span className="text-primary font-medium">+{formatAUD(roundup)}</span>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            if (c.type === 'nospend') {
              const days = c.noSpendDays ?? [];
              const thisMonthDays = days.filter(d => d.startsWith(currentMonth));
              const target = c.noSpendTarget ?? 20;
              const streak = getNoSpendStreak(days);
              const todayMarked = days.includes(today);
              const pct = (thisMonthDays.length / target) * 100;

              return (
                <Card key={c.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Flame className={cn('h-4 w-4', streak >= 3 ? 'text-warning' : 'text-muted-foreground')} />
                        {c.name}
                      </CardTitle>
                      <button onClick={() => deleteChallenge(c.id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {thisMonthDays.length}/{target} days this month
                    </p>
                  </CardHeader>
                  <CardContent>
                    <Progress value={Math.min(pct, 100)} className="h-2 mb-3" />
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Flame className={cn('h-5 w-5', streak >= 7 ? 'text-destructive' : streak >= 3 ? 'text-warning' : 'text-muted-foreground')} />
                        <div>
                          <p className="text-sm font-semibold">{streak} day streak</p>
                          <p className="text-xs text-muted-foreground">
                            {streak >= 7 ? '🔥 On fire!' : streak >= 3 ? '💪 Keep going!' : 'Start a streak!'}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={todayMarked ? 'secondary' : 'default'}
                        className="gap-1"
                        onClick={() => toggleNoSpendToday(c.id)}
                      >
                        {todayMarked ? <><Check className="h-3.5 w-3.5" /> Done</> : <><Plus className="h-3.5 w-3.5" /> Today</>}
                      </Button>
                    </div>

                    {/* Mini calendar for current month */}
                    <div className="grid grid-cols-7 gap-1 text-center">
                      {['M','T','W','T','F','S','S'].map((d, i) => (
                        <span key={i} className="text-[10px] text-muted-foreground font-medium">{d}</span>
                      ))}
                      {(() => {
                        const year = parseInt(currentMonth.split('-')[0]);
                        const month = parseInt(currentMonth.split('-')[1]) - 1;
                        const firstDay = new Date(year, month, 1).getDay();
                        const daysInMonth = new Date(year, month + 1, 0).getDate();
                        const offset = firstDay === 0 ? 6 : firstDay - 1; // Mon=0
                        const cells = [];
                        for (let i = 0; i < offset; i++) cells.push(<div key={`e${i}`} />);
                        for (let d = 1; d <= daysInMonth; d++) {
                          const dateStr = `${currentMonth}-${String(d).padStart(2, '0')}`;
                          const marked = days.includes(dateStr);
                          const isToday = dateStr === today;
                          cells.push(
                            <div
                              key={d}
                              className={cn(
                                'h-6 rounded text-[10px] flex items-center justify-center font-medium',
                                marked ? 'bg-success text-success-foreground' :
                                isToday ? 'bg-primary/10 text-primary ring-1 ring-primary/30' :
                                'bg-muted/50 text-muted-foreground'
                              )}
                            >
                              {d}
                            </div>
                          );
                        }
                        return cells;
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
}
